import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { StandardButton } from '../../../../../component/common/StandardUI';
import alarmClockIcon from '../../../../../assets/svg/icons/Alarm-Clock--Streamline-Flex.svg';

/**
 * Centred "coming soon" dialog for People Plan modules that are not ready yet.
 * Nothing is removed from the app — the feature's pages and routes stay in
 * place; this simply stands in front of the entry point.
 */
const ComingSoonModal = ({ open, title, description, buttonText, onClose }) => (
  <Transition.Root appear show={open} as={Fragment}>
    <Dialog as="div" className="relative z-50" onClose={onClose}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </Transition.Child>

      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="relative w-full sm:w-[500px] md:w-[560px] transform overflow-hidden rounded-3xl bg-white dark:bg-shoorah-darkBgTabColor shadow-xl transition-all px-8 py-12 sm:px-16">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-5 right-5 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              <div className="flex justify-center">
                <img src={alarmClockIcon} alt="" className="w-16 h-16 dark:invert" />
              </div>

              <Dialog.Title className="text-2xl font-medium text-center mt-6 text-gray-800 dark:text-white">
                {title}
              </Dialog.Title>

              <p className="text-base text-center text-gray-500 dark:text-gray-300 mt-4">
                {description}
              </p>

              <div className="flex justify-center mt-8">
                <StandardButton text={buttonText} onClick={onClose} />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </div>
    </Dialog>
  </Transition.Root>
);

ComingSoonModal.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  buttonText: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

ComingSoonModal.defaultProps = {
  title: 'Coming soon',
  description: 'This feature is not quite ready yet. We will let you know as soon as it is.',
  buttonText: 'Got it'
};

export default ComingSoonModal;
